// Path: apps/0001-estata.com/frontend/src/components/LoginBackdrop.tsx
// v1.2.0 - Nur noch die Glasfassade (Inhaber 27.10.2026: "Nimm nur den Rest der Fotos raus") - mit einem Bild
//          kein Wechsel; Taenzerin und Bauleiter raus (Taenzerin bleibt Bild der PIN-Seite).
// v1.1.0 - Helle Fotos mit leichtem Verlauf oben/unten (shade) - weisses Logo und Satz waren darauf kaum lesbar.
// v1.0.0 - Hintergrund der Login-Startseite mit wechselnden Fotos (Inhaber 27.10.2026: "verschiedene Fotos ... die
//          sollten mit zum Fade smooth wechseln"): jedes Bild treibt langsam (login-drift), alle LOGIN_IMAGE_MS
//          blendet das naechste weich darueber (LOGIN_FADE_MS). Das naechste Bild wird erst gezeigt, wenn es geladen
//          ist - nie ein schwarzes Loch. "Bewegung reduzieren": kein Treiben, Bilder wechseln trotzdem (nur Blende).
//          Neues Foto: Datei nach public/images/login/, Pfad in LOGIN_IMAGES eintragen.
//          Fotos des Inhabers (27.10.2026): Bauleiter mit Helm + Daten, Glasfassade mit Baum.

import { useEffect, useState } from 'react';

export interface LoginImage {
  src: string;
  /** Helles Foto: oben und unten leicht abgedunkelt, damit Logo und weisse Schrift lesbar bleiben.
   *  Das Startbild bleibt unveraendert (Inhaber 10.10.2026: "das Hintergrundbild so lassen, wie es ist"). */
  shade?: boolean;
}

/** Reihenfolge = Reihenfolge der Anzeige; das erste Bild steht sofort. Ab zwei Bildern wird gewechselt. */
export const LOGIN_IMAGES: LoginImage[] = [
  { src: '/images/login/glass-facade.jpg', shade: true },
];

// Nur wo Text steht: oben hinter dem Logo, unten hinter Satz und Knoepfen; die Mitte bleibt das Foto
const SHADE = 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.32) 16%, rgba(0,0,0,0) 32%, rgba(0,0,0,0) 42%, rgba(0,0,0,0.55) 64%, rgba(0,0,0,0.7) 100%)';
export const LOGIN_IMAGE_MS = 9000;
export const LOGIN_FADE_MS = 1800;

function preload(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
}

export default function LoginBackdrop({ images = LOGIN_IMAGES }: { images?: LoginImage[] }) {
  const [index, setIndex] = useState(0);
  // Das bisherige Bild bleibt voll stehen, bis das neue darueber ganz eingeblendet ist - kein dunkles Zwischenbild
  const [prev, setPrev] = useState<number | null>(null);
  // Nur Bilder, die schon dran waren, stehen im DOM - ein Hintergrundbild laedt sonst sofort, auch unsichtbar
  const [seen, setSeen] = useState<number[]>([0]);

  useEffect(() => {
    if (images.length < 2) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const next = (index + 1) % images.length;
      await preload(images[next].src);
      if (cancelled) return;
      setSeen((s) => (s.includes(next) ? s : [...s, next]));
      // erst im naechsten Bild umschalten, damit die neue Ebene mit opacity 0 steht und die Blende laeuft
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!cancelled) { setPrev(index); setIndex(next); }
      }));
    }, LOGIN_IMAGE_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [index, images]);

  // Nach der Blende verschwindet das alte Bild still hinter dem neuen - kommt es wieder dran, blendet es von 0 auf
  useEffect(() => {
    if (prev === null) return undefined;
    const t = window.setTimeout(() => setPrev(null), LOGIN_FADE_MS + 100);
    return () => window.clearTimeout(t);
  }, [prev]);

  return (
    <div className="absolute inset-0" aria-hidden data-login-backdrop>
      {images.map(({ src, shade }, i) => seen.includes(i) && (
        <div key={src} data-login-image={i} data-active={i === index || undefined}
             className="absolute inset-0 bg-cover bg-center login-drift"
             style={{
               backgroundImage: `url('${src}')`,
               opacity: i === index || i === prev ? 1 : 0,
               zIndex: i === index ? 2 : i === prev ? 1 : 0,
               transition: i === index ? `opacity ${LOGIN_FADE_MS}ms ease-in-out` : 'none',
             }}>
          {shade && <div className="absolute inset-0" style={{ backgroundImage: SHADE }} />}
        </div>
      ))}
    </div>
  );
}
